import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { log } from '@/app/shared/utils/logger';
import notification from '@app/shared/utils/notification';
import { Button, Image } from '@app/shared/ui/atoms/Common';
import { fortunePointer, fortuneWheel, fortuneWheelEN } from '@app/assets';
import { SpinWheelResponse, WheelRewardType } from '@app/store/slices/users/usersTypes';
import { useSpinWheelRewardMutation } from '@app/store/slices/users/usersApi';
import { DOMError, isReduxError } from '@app/shared/constants/error';

interface FortuneWheelProps {
  size?: number;
  duration?: number;
  onFinish: (reward: SpinWheelResponse) => void;
}

type SpinPhase = 'idle' | 'loading' | 'stopping';

function getRotationDeg(el: HTMLElement): number {
  const transform = window.getComputedStyle(el).transform;
  if (!transform || transform === 'none') return 0;

  const match = /matrix\(([^)]+)\)/.exec(transform);
  if (!match) {
    return 0;
  }

  const [a, b] = match[1].split(',').map((v) => parseFloat(v.trim()));
  let angle = Math.atan2(b, a) * (180 / Math.PI);
  if (angle < 0) angle += 360;
  return angle;
}

/**
 * IMPORTANT:
 * The order of the rewards in the wheel image must match this array.
 */
const WHEEL_ORDER: WheelRewardType[] = Object.values(WheelRewardType);

export function FortuneWheel({ size = 340, duration = 4, onFinish }: FortuneWheelProps) {
  const { t, i18n } = useTranslation();
  const lang = i18n.resolvedLanguage ?? 'ru';

  // wrapper is rotated by the "final" transform-transition
  const wheelWrapperRef = useRef<HTMLDivElement | null>(null);
  // inner rotates infinitely via animation while waiting for the API
  const wheelSpinnerRef = useRef<HTMLDivElement | null>(null);

  const [rotation, setRotation] = useState(0);
  const [phase, setPhase] = useState<SpinPhase>('idle');
  const [stopTransitionEnabled, setStopTransitionEnabled] = useState(false);

  const [spinWheelReward] = useSpinWheelRewardMutation();

  const isSpinning = phase !== 'idle';

  const spin = async () => {
    if (isSpinning) return;

    setPhase('loading');
    setStopTransitionEnabled(false);

    let reward: SpinWheelResponse | null = null;

    try {
      const res = await spinWheelReward().unwrap();

      if (!res.ok) {
        throw new DOMError('Server error', res);
      }

      reward = res.data ?? null;
    } catch (error: unknown) {
      setPhase('idle');
      if (isReduxError(error)) {
        const { status, data } = error;
        if (status === 400 && typeof data?.data.previousSpinAt === 'string') {
          notification.error(
            t('errors.already_spun_today', {
              time: new Date(data.data.previousSpinAt).toLocaleString('ru-RU', {
                hour12: false,
                second: undefined,
              }),
            })
          );
          return;
        }
      }
      notification.error(t('errors.general'));
      void log({ level: 'error', message: 'spinFortuneWheel error', error });
      return;
    }

    if (!reward?.rewardType) {
      setPhase('idle');
      notification.error(t('errors.general'));
      return;
    }
    const rewardType = reward.rewardType;

    const wrapperEl = wheelWrapperRef.current;
    const spinnerEl = wheelSpinnerRef.current;
    if (!wrapperEl || !spinnerEl) {
      setPhase('idle');
      notification.error(t('errors.general'));
      return;
    }

    const spinnerAngle = getRotationDeg(spinnerEl);
    const currentTotalRotation = rotation + spinnerAngle;

    setPhase('stopping');
    setStopTransitionEnabled(false);
    setRotation(currentTotalRotation);

    requestAnimationFrame(() => {
      const rewardIndex = WHEEL_ORDER.indexOf(rewardType);

      if (rewardIndex < 0) {
        setPhase('idle');
        notification.error(t('errors.general'));
        return;
      }

      const anglePer = 360 / WHEEL_ORDER.length;
      const offsetDeg = 0;

      const centerAngle = rewardIndex * anglePer + anglePer / 2 + offsetDeg;

      const pointerAngle = 90;
      const normalized = ((currentTotalRotation % 360) + 360) % 360;
      const currentPointerAngle = (pointerAngle - normalized + 360) % 360;

      const delta = (currentPointerAngle - centerAngle + 360) % 360;
      const fullSpins = 5;

      const finalRotation = currentTotalRotation + fullSpins * 360 + delta;

      const onEnd = () => {
        setTimeout(() => {
          setPhase('idle');
          setStopTransitionEnabled(false);
          onFinish(reward);
        }, 1000);
      };

      wrapperEl.addEventListener('transitionend', onEnd, { once: true });

      setStopTransitionEnabled(true);
      setRotation(finalRotation);
    });
  };

  return (
    <div className="flex flex-col items-center gap-6">
      <div className="relative -left-[14px] xs:left-0" style={{ width: size, height: size }}>
        <Image
          src={fortunePointer}
          className="absolute top-1/2 right-[-20px] -translate-y-1/2 z-10"
          style={{ width: size * 0.15 }}
        />

        <div
          ref={wheelWrapperRef}
          className="rounded-full"
          style={{
            width: size,
            height: size,
            transform: `rotate(${String(rotation)}deg)`,
            transition:
              phase === 'stopping' && stopTransitionEnabled
                ? `transform ${String(duration)}s cubic-bezier(.17,.67,.34,1)`
                : 'none',
          }}
        >
          <div
            ref={wheelSpinnerRef}
            className={`rounded-full bg-cover bg-center ${phase === 'loading' ? 'animate-spin' : ''}`}
            style={{
              width: '100%',
              height: '100%',
              backgroundImage: `url(${lang === 'en' ? fortuneWheelEN : fortuneWheel})`,
            }}
          />
        </div>

        {phase === 'idle' && (
          <Button
            onClick={spin}
            variant="custom"
            disabled={isSpinning}
            className="transition w-[79px] h-[79px] rounded-full absolute top-1/2 left-1/2 -translate-x-1/2 transform -translate-y-1/2 bg-accent"
          >
            {t('common.lets_go')}
          </Button>
        )}
      </div>
    </div>
  );
}
